import SelectField from '@pelckmans/elementis/components/select-field';
import { FocusEventHandler, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchTeacherProfile, SubjectYear } from '../../../api/my-account';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { sortByName } from '../../../utils/sortByName';
import { fetchSubjects, Subject } from '../api/MyGroupsApi';

type Props = {
  onChange: (subjects: { id: string; name: string }[]) => void;
  value: { id: string; name: string }[];
  errorText?: string;
  onBlur: FocusEventHandler<HTMLElement>;
};

function SubjectSearchSelector({ onChange, onBlur, value, errorText }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const handleError = useThrowAsyncError();

  const [isLoading, setIsLoading] = useState(true);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [searchValue, onSearchValueChange] = useState('');
  const [subjectYears, setSubjectYears] = useState<SubjectYear[]>([]);

  useEffect(() => {
    const getSubjects = async () => {
      const fetchedSubjects = await fetchSubjects();

      setSubjects(fetchedSubjects.sort(sortByName(language)));
    };

    getSubjects()
      .catch(handleError)
      .finally(() => setIsLoading(false));
  }, [handleError, language]);

  useEffect(() => {
    const getSubjectYears = async () => {
      const { subjectYears: fetchedSubjectYears } = await fetchTeacherProfile();

      setSubjectYears(
        fetchedSubjectYears.sort((a, b) => {
          return a.subject.name
            .toLowerCase()
            .localeCompare(b.subject.name.toLowerCase(), language, { sensitivity: 'base' });
        }),
      );
    };

    getSubjectYears().catch(handleError);
  }, [handleError, language]);

  const subjectsJsx = useMemo(() => {
    if (subjects.length === 0 && subjectYears.length === 0) {
      return [];
    }

    const filteredSubjectYears = subjectYears.filter(x =>
      x.subject.name.toLowerCase().includes(searchValue.toLowerCase()),
    );

    if (subjectYears.length === 0 || filteredSubjectYears.length === 0) {
      return subjects
        .filter(x => x.name.toLowerCase().includes(searchValue.toLowerCase()))
        .map(subject => <SelectField.Item key={subject.id} value={subject} text={subject.name} />);
    }

    return (
      <>
        {filteredSubjectYears.length > 0 && (
          <SelectField.Group
            title={t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.SUBJECT.LISTS.MY_SUBJECTS.TITLE')}
          >
            {filteredSubjectYears.map(({ subject }) => (
              <SelectField.Item key={subject.id} value={subject} text={subject.name} />
            ))}
          </SelectField.Group>
        )}
        {subjects.length > 0 && (
          <SelectField.Group
            title={t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.SUBJECT.LISTS.SUBJECTS.TITLE')}
          >
            {subjects
              .filter(x => x.name.toLowerCase().includes(searchValue.toLowerCase()))
              .map(subject => (
                <SelectField.Item key={subject.id} value={subject} text={subject.name} />
              ))}
          </SelectField.Group>
        )}
      </>
    );
  }, [searchValue, subjects, subjectYears, t]);

  return (
    <SelectField
      required
      multiple
      searchable
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={!!errorText}
      errorText={errorText}
      isLoading={isLoading}
      renderValueContent={option => option.name}
      selectedValueLabel={t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.SUBJECT.SELECTED_LABEL')}
      placeholder={t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.SUBJECT.PLACEHOLDER')}
      labelText={t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.SUBJECT.LABEL')}
      dropdownMenuProps={{
        ariaLabel: t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.SUBJECT.A11Y_LABEL'),
      }}
      searchProps={{
        searchValue,
        onSearchValueChange,
        inputProps: {
          ariaLabel: t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.SUBJECT.SEARCH.A11Y_LABEL'),
        },
      }}
      matchOn="id"
      flip
    >
      {subjectsJsx}
    </SelectField>
  );
}

export default SubjectSearchSelector;
